<script setup>
import '@awesome.me/webawesome/dist/components/toast/toast.js'
import '@awesome.me/webawesome/dist/components/toast-item/toast-item.js'

import { computed, nextTick, onMounted, onUnmounted, ref, watchEffect } from 'vue'

import BankPullFailureToastContent from '~/components/bank-pull-status/BankPullFailureToastContent.vue'
import { sessionStorageService } from '~/libs/session-storage.service'
import { useApplicationConfigStore } from '~/store/application-config'
import { useBankPullStatusUiStore } from '~/store/bank-pull-status-ui'
import { useBorrowerStore } from '~/store/borrower'

const applicationConfigStore = useApplicationConfigStore()
const bankPullStatusUiStore = useBankPullStatusUiStore()
const borrowerStore = useBorrowerStore()

function dismissalKey(borrowerId) {
  return `bank_pull_failure_toast_dismissed_${borrowerId}`
}

// BR-7: dismissal is scoped by borrowerId, unlike the unscoped fs_* analytics precedent, so one
// business's dismissal can never suppress a different business's failure notice.
function isDismissedForCurrentBorrower() {
  const borrowerId = borrowerStore.borrowerId
  return Boolean(borrowerId) && Boolean(sessionStorageService.getItem(dismissalKey(borrowerId)))
}

// This host renders its own <wa-toast-item> (no shared toast service), so "showing" the toast is
// just this instance's `toastVisible`. The watchEffect below waits for `mounted` before claiming
// the shared flag: inside a pending <Suspense> branch (where NuxtLayout mounts an incoming layout,
// and marketplace/embedded have an async setup()) an instance can run its effects long before it
// is actually on screen, and claiming the flag then would mark a toast "shown" that nobody sees.
//
// The shown/hidden flag lives in bankPullStatusUiStore, shared across every mounted host
// instance (portal/marketplace/embedded each mount their own) - a per-instance flag here let two
// concurrently-mounted instances (e.g. a brief layout-transition overlap) each independently
// observe "no toast shown yet" and both render one, producing two stacked duplicate toasts for one
// failure. markFailureToastShown()/markFailureToastHidden() return true only for the instance
// whose call actually transitions the shared flag, so only one instance ever acts.
const toastVisible = ref(false)
// Rendered as a keyed v-for of 0/1 items, not v-if, like UiToastHost: a closed <wa-toast-item> removes
// itself from the DOM before wa-after-hide, and swapping a v-if branch for its comment anchor would
// ask Vue to insert into that detached item's (null) parent. A keyed list's container is <wa-toast>.
const toastItemKeys = computed(() => (toastVisible.value ? ['bank-pull-failure'] : []))
const hostId = Symbol('bank-pull-failure-toast-host')
// Set when a new failure claims the toast while a dismissed item is still animating out (e.g. a
// business switch lands between wa-hide and wa-after-hide). That item is about to remove itself,
// so handleAfterHide renders a fresh one for the new claim.
let reclaimedWhileClosing = false
// True between the borrower's close (wa-hide) and wa-after-hide: that item removes itself.
let closing = false

function showFailureToast() {
  if (!bankPullStatusUiStore.markFailureToastShown(hostId)) return
  if (toastVisible.value) reclaimedWhileClosing = true
  // BR-6: duration="0" on the item, so it stays until the borrower closes it.
  toastVisible.value = true
}

function handleAfterHide() {
  closing = false
  toastVisible.value = false
  if (!reclaimedWhileClosing) return
  reclaimedWhileClosing = false
  nextTick(() => {
    if (bankPullStatusUiStore.failureToastShown) toastVisible.value = true
  })
}

// Every host drops its own copy, not only the one whose call flips the shared flag: a host that
// mounts late inside a <Suspense> can run this effect first and clear the flag for the holder.
function hideFailureToast() {
  reclaimedWhileClosing = false
  bankPullStatusUiStore.markFailureToastHidden()
  if (closing) return
  nextTick(() => {
    if (!bankPullStatusUiStore.failureToastShown) toastVisible.value = false
  })
}

// BR-7: records a per-borrower dismissal so the toast is not re-raised this session, including
// across route changes and remounts. Bound to the item's close (wa-hide); a programmatic clear
// drops the item from the list instead, which fires no event.
function handleUserDismiss() {
  closing = true
  bankPullStatusUiStore.markFailureToastHidden()
  const borrowerId = borrowerStore.borrowerId
  if (borrowerId) sessionStorageService.setItem(dismissalKey(borrowerId), '1')
}

// Identifies this instance to the store, which tracks which mounted hosts hold a copy of the
// shown toast. NuxtLayout swaps layouts through a <Suspense>, where the incoming layout's host
// mounts BEFORE the outgoing one unmounts - and holds no copy, since it mounted after the toast
// was shown. When the outgoing host (the only holder) unmounts, the store clears the shared flag
// instead of leaving it set, and this instance's own live watchEffect below raises the toast
// again in its own <wa-toast>. An instance that was co-mounted when the toast was shown keeps its
// own copy, so an earlier instance unmounting then leaves everything untouched.
// Becomes true in onMounted. The watchEffect below does nothing before then, so it never claims
// the shared flag for a toast that isn't on screen yet.
const mounted = ref(false)

onMounted(() => {
  bankPullStatusUiStore.registerFailureToastHost(hostId)
  mounted.value = true
})
// This instance's own toast leaves with its DOM; the store decides whether another host re-raises it.
onUnmounted(() => {
  bankPullStatusUiStore.unregisterFailureToastHost(hostId)
})

// watchEffect (not watch): a borrower switch that lands back on indicatorState === 'failed' must
// still re-check the new borrower's own dismissal key even when the derived string never changes
// value across the switch - same reasoning as store/bank-pull-status-ui.js's own watchEffect.
// BR-8: hasRequiredBankStatementsManual turning true clears indicatorState (via promptEligible),
// which this effect reads directly, so the toast is cleared and not raised again. (The mount-order
// race this used to risk is handled by waiting for `mounted`, not by this watcher's flush timing.)
watchEffect(() => {
  if (!mounted.value) return

  const shouldPrompt = applicationConfigStore.isBankPullStatusIndicatorEnabled
    && bankPullStatusUiStore.indicatorState === 'failed'
  void borrowerStore.borrowerId

  if (shouldPrompt) {
    if (!isDismissedForCurrentBorrower()) showFailureToast()
  } else {
    hideFailureToast()
  }
})

// Same responsive rule as the app's other toasts (UiToastHost): this toast is persistent (no
// auto-dismiss, per BR-6), so leaving it pinned top-right on a small viewport for the rest of the
// session would be more noticeable than a transient toast.
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)
function handleResize() {
  windowWidth.value = window.innerWidth
}
onMounted(() => window.addEventListener('resize', handleResize))
onUnmounted(() => window.removeEventListener('resize', handleResize))
const isMobile = computed(() => windowWidth.value < 768)

// <wa-toast> closes its newest item on any Escape keypress anywhere in the document, which here
// would also record a permanent dismissal (wa-hide → handleUserDismiss) when the borrower only
// meant to close the bank-pull popover. On main this toast closes only from its close button, so
// detach that document listener for this stack (the app's other toasts keep Web Awesome's default).
const toastStack = ref(null)
onMounted(async () => {
  if (typeof customElements === 'undefined' || !customElements.get('wa-toast')) return
  await customElements.whenDefined('wa-toast')
  const stack = toastStack.value
  if (stack?.handleDocumentKeyDown) document.removeEventListener('keydown', stack.handleDocumentKeyDown)
})
</script>

<template>
  <wa-toast
    ref="toastStack"
    class="bank-pull-failure-toast-host"
    :class="isMobile ? 'bank-pull-failure-toast-host--mobile' : 'bank-pull-failure-toast-host--desktop'"
    :placement="isMobile ? 'bottom-center' : 'top-end'"
    data-cy="bank-pull-failure-toast-host"
  >
    <!-- Its own stack, not the shared UiToastHost: different position (below the fixed nav bars) and a
         white card look, scoped to this toast so the app's other toasts keep their styling.
         Kept inside the root: a root-level comment gives dev builds (and unit tests) a Fragment root
         that Vue fully diffs, hiding patch errors only the single-root production build hits. -->
    <wa-toast-item
      v-for="key in toastItemKeys"
      :key="key"
      class="bank-pull-failure-toast-item"
      variant="danger"
      duration="0"
      @wa-hide.self="handleUserDismiss"
      @wa-after-hide.self="handleAfterHide"
    >
      <BankPullFailureToastContent />
    </wa-toast-item>
  </wa-toast>
</template>

<style scoped>
/* Measured on main (PrimeVue): 24rem wide (max 90vw), top 110px clears the marketplace/embedded/
   portal fixed nav bars (~85-90px), 20px from the right; phones: bottom-centred. White card, 6px
   radius, 1px red-300 border with a 4px red-300 left accent, no shadow, neutral round close button. */
/* wa-toast is itself the fixed stack: drop its padding and place it ourselves (main: 20px + the
   message's 16px bottom margin on phones). */
.bank-pull-failure-toast-host {
  padding: 0;
}
.bank-pull-failure-toast-host--desktop {
  --width: min(24rem, 90vw);
  inset-block-start: 110px;
  inset-inline-end: 20px;
}
.bank-pull-failure-toast-host--mobile {
  width: 90vw;                         /* wa-toast goes full-width on small screens; main is 90vw */
  inset-block-end: 36px;
}
.bank-pull-failure-toast-item {
  --accent-width: 0;
}
.bank-pull-failure-toast-item::part(toast-item) {
  border: 1px solid var(--tw-color-red-300);
  border-inline-start-width: 4px;
  border-radius: 0.375rem;
  background-color: var(--tw-color-white);
  box-shadow: none;
  color: var(--tw-color-w-neutral-900);
}
.bank-pull-failure-toast-item::part(icon) {
  display: none;
}
/* Main: text 16px in from the accent, 53px clear of the right edge (close column). */
.bank-pull-failure-toast-item::part(content) {
  padding-inline: 16px 21px;
}
/* Top-right 24px circle (main: 13px from the top, 7px from the right edge), not a full-height strip. */
.bank-pull-failure-toast-item::part(close-button) {
  align-self: flex-start;
  width: 1.5rem;
  height: 1.5rem;
  margin: 13px 7px 0 0;
  padding: 0;
  border-radius: 9999px;
  color: var(--tw-color-w-neutral-500);
}
.bank-pull-failure-toast-item::part(close-button):hover {
  background-color: var(--tw-color-w-neutral-100);
}
.bank-pull-failure-toast-item::part(close-button):focus-visible {
  outline: 1px solid var(--tw-color-w-neutral-400);
  outline-offset: 2px;
}
</style>
